import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    phone: '',
    email: '',
    website: '',
    nmls: '',
    bio: ''
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/team`, { headers });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/team`, formData, { headers });
      setTeamMembers([...teamMembers, response.data]);
      setIsCreating(false);
      resetForm();
    } catch (error) {
      console.error('Error creating team member:', error);
      alert('Error creating team member');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.put(`${API}/admin/team/${id}`, formData, { headers });
      setTeamMembers(teamMembers.map(member => 
        member.id === id ? response.data : member
      ));
      setEditingMember(null);
      resetForm();
    } catch (error) {
      console.error('Error updating team member:', error);
      alert('Error updating team member');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/team/${id}`, { headers });
      setTeamMembers(teamMembers.filter(member => member.id !== id));
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Error deleting team member');
    }
  };

  const handlePhotoUpload = async (id, file) => {
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/team/${id}/photo`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Update team member with new photo URL
      setTeamMembers(teamMembers.map(member => 
        member.id === id ? { ...member, photo: response.data.photo_url } : member
      ));
      
      alert('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo');
    }
  };

  const startEdit = (member) => {
    setEditingMember(member.id);
    setFormData({
      name: member.name,
      title: member.title,
      phone: member.phone,
      email: member.email,
      website: member.website,
      nmls: member.nmls,
      bio: member.bio
    });
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setIsCreating(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      phone: '',
      email: '',
      website: '',
      nmls: '',
      bio: ''
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isLoading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2c4a] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading team members...</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/admin" className="flex items-center space-x-2">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Dashboard</span>
                </Link>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
            </div>
            <Button
              className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Create Form */}
        {isCreating && (
          <Card className="mb-8 border-none bg-white">
            <CardHeader>
              <CardTitle>Add New Team Member</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g. Senior Loan Officer"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@pathwayhome.com"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://pathwayhome.com/profile"
                  />
                </div>
                <div>
                  <Label htmlFor="nmls">NMLS Number</Label>
                  <Input
                    id="nmls"
                    name="nmls"
                    value={formData.nmls}
                    onChange={handleInputChange}
                    placeholder="123456"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Professional bio and experience..."
                    rows={4}
                  />
                </div>
              </div>
              <div className="flex space-x-4 mt-6">
                <Button
                  className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                  onClick={handleCreate}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Team Member
                </Button>
                <Button variant="outline" onClick={cancelEdit}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Members List */}
        <div className="grid gap-6">
          {teamMembers.map((member) => (
            <Card key={member.id} className="border-none bg-white">
              <CardContent className="p-6">
                {editingMember === member.id ? (
                  // Edit Form
                  <div>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <Label htmlFor={`name-${member.id}`}>Name</Label>
                        <Input
                          id={`name-${member.id}`}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`title-${member.id}`}>Title</Label>
                        <Input
                          id={`title-${member.id}`}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`phone-${member.id}`}>Phone</Label>
                        <Input
                          id={`phone-${member.id}`}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`email-${member.id}`}>Email</Label>
                        <Input
                          id={`email-${member.id}`}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`website-${member.id}`}>Website</Label>
                        <Input
                          id={`website-${member.id}`}
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`nmls-${member.id}`}>NMLS Number</Label>
                        <Input
                          id={`nmls-${member.id}`}
                          name="nmls"
                          value={formData.nmls}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor={`bio-${member.id}`}>Bio</Label>
                        <Textarea
                          id={`bio-${member.id}`}
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          rows={4}
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <Button
                        className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                        onClick={() => handleUpdate(member.id)}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={cancelEdit}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-start space-x-4">
                        {/* Photo Section */}
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300">
                            {member.photo ? (
                              <img 
                                src={member.photo.startsWith('http') ? member.photo : `${BACKEND_URL}${member.photo}`} 
                                alt={member.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">Error Loading Photo</div>';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                No Photo
                              </div>
                            )}
                          </div>
                          <div className="mt-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  handlePhotoUpload(member.id, file);
                                }
                              }}
                              className="hidden"
                              id={`photo-upload-${member.id}`}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => document.getElementById(`photo-upload-${member.id}`).click()}
                            >
                              Upload Photo
                            </Button>
                          </div>
                        </div>
                        
                        {/* Member Info */}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                          <p className="text-[#0b2c4a] font-medium">{member.title}</p>
                          <p className="text-sm text-gray-600">NMLS {member.nmls}</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(member)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Phone: {member.phone}</p>
                        <p className="text-sm text-gray-600">Email: {member.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Website: {member.website}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">{member.bio}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {teamMembers.length === 0 && (
          <Card className="border-none bg-white">
            <CardContent className="p-12 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Team Members</h3>
              <p className="text-gray-600 mb-4">Get started by adding your first team member.</p>
              <Button
                className="bg-[#0b2c4a] hover:bg-[#0b2c4a]/90 text-white"
                onClick={() => setIsCreating(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Team Member
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TeamAdmin;